import useAmplitudeContext from "@/hooks/useAmplitudeContext";
import { useRouter } from "next/router";
import usePreviousRoute from "./usePreviousRoute";
import useRequestId from "@/hooks/useRequestId";
import { atom, useAtom } from "jotai";
import { useAnalytics } from "@/hooks/useAnalytics";

export const INTERNAL_ORIGIN_KEY = "plush-internal-origin";
export const preferenceOriginAtom = atom<"site-open" | "popup" | "banner">(
  "site-open"
);
export const preferenceBannerShowedAtom = atom(false);

export const pagePathnameConvention = (pathname: string) => {
  if (pathname === "/") {
    return "home";
  }
  if (pathname === "/results/[search]") {
    return "srp";
  }
  if (pathname === "/edits/[search]") {
    return "edits";
  }
  if (pathname === "/product/[name]/[id]") {
    return "pdp";
  }
  if (pathname === "/brand/[name]") {
    return "brand";
  }
  if (pathname === "/collections/[id]") {
    return "collection";
  }
  if (pathname === "/blog/[seoURL]") {
    return "blog";
  }
  return pathname ? pathname.substring(1, pathname.length) : "";
};

export const pageAsPathConvention = (asPath: string) => {
  if (asPath === "/") {
    return "home";
  }
  if (asPath.includes("/product/")) {
    return "pdp";
  }
  if (asPath.endsWith("/blogs")) {
    return "blogs";
  }
  if (asPath.includes("/blog/")) {
    return "blog";
  }
  if (asPath.endsWith("/brands")) {
    return "brands";
  }
  if (asPath.includes("/brand/")) {
    return "brand";
  }
  if (asPath.includes("/results/")) {
    return "srp";
  }
  if (asPath.includes("/edits/")) {
    return "edits";
  }
  if (asPath.includes("/collections/")) {
    return "collection";
  }
  if (asPath.includes("/preference/")) {
    return "preference";
  }
  return asPath;
};

export const parsePage = (page: string | null, section?: string) => {
  if (!section) {
    return page;
  }
  let result = page;
  if (section.toLowerCase().includes("similar")) {
    result += "_similar_items";
  } else if (section.toLowerCase().includes("related")) {
    result += "_related_items";
  }
  return result;
};

const useTracker = () => {
  const { trackAmplitudeEvent } = useAmplitudeContext();
  const previousRoute = usePreviousRoute();
  const router = useRouter();
  const { requestId } = useRequestId();
  const [preferenceOrigin] = useAtom(preferenceOriginAtom);
  const analytics = useAnalytics();

  const getCurrentPage = () => {
    return pagePathnameConvention(router.pathname);
  };

  const getPreviousPage = () => {
    return pageAsPathConvention(previousRoute || "");
  };

  const getRequestId = () => {
    const page = getCurrentPage();
    return page === "srp" || page === "edits" ? requestId : undefined;
  };

  const saveInternalOriginLocal = () => {
    if (localStorage) {
      localStorage.setItem(INTERNAL_ORIGIN_KEY, getCurrentPage());
    }
  };

  const getInternalOrigin = () => {
    if (previousRoute) {
      return getPreviousPage();
    } else if (localStorage) {
      const origin = localStorage.getItem(INTERNAL_ORIGIN_KEY);
      localStorage.setItem(INTERNAL_ORIGIN_KEY, "");
      return origin;
    }
    return "";
  };

  const searchEvent = (eventProperties: {
    query: string;
    smart_filter_content: string;
    original_query: string;
  }) => {
    trackAmplitudeEvent("search", {
      ...eventProperties,
      internal_origin: getCurrentPage(),
    });
    analytics.trackSearch({
      query: eventProperties.query,
    });
  };

  const rerunEvent = (eventProperties: { query: string }) => {
    trackAmplitudeEvent("rerun", {
      ...eventProperties,
      internal_origin: getCurrentPage(),
    });
  };

  const srpViewEvent = (eventProperties: {
    query: string;
    error_type: "NOT_SUPPORTED" | "ZERO_RESULTS" | null;
    referrer?: string;
    request_id: string;
  }) => {
    trackAmplitudeEvent("search_results_page_view", {
      ...eventProperties,
      internal_origin: getInternalOrigin(),
    });
  };

  const editsPageViewEvent = (eventProperties: {
    query: string;
    error_type: "NOT_SUPPORTED" | "ZERO_RESULTS" | null;
    referrer?: string;
    request_id: string;
  }) => {
    trackAmplitudeEvent("edits_page_view", {
      ...eventProperties,
      internal_origin: getInternalOrigin(),
    });
  };

  const homePageViewEvent = (eventProperties: { referrer?: string }) => {
    trackAmplitudeEvent("home_page_view", eventProperties);
  };

  const aboutPageViewEvent = (eventProperties: { referrer?: string }) => {
    trackAmplitudeEvent("about_page_view", eventProperties);
  };

  const brandPageViewEvent = (eventProperties: {
    brand_id: string;
    brand_name: string;
    referrer?: string;
  }) => {
    trackAmplitudeEvent("brand_page_view", {
      ...eventProperties,
      internal_origin: getInternalOrigin(),
    });
  };

  const loginViewEvent = () => {
    trackAmplitudeEvent("login_page_view", {
      internal_origin: getInternalOrigin(),
    });
  };

  const productPageViewEvent = (eventProperties: {
    brand: string;
    product_id: string;
    original_price: number;
    price: number;
    product_name: string;
    retailer: string;
    seo_url: string;
    referrer?: string;
  }) => {
    trackAmplitudeEvent("product_page_view", {
      ...eventProperties,
      internal_origin: getInternalOrigin(),
    });
  };

  const blogsHomePageViewEvent = (eventProperties: { referrer?: string }) => {
    trackAmplitudeEvent("blogs_home_page_view", eventProperties);
  };

  const blogArticlePageViewEvent = (eventProperties: {
    id: string;
    referrer?: string;
  }) => {
    trackAmplitudeEvent("blog_article_page_view", eventProperties);
  };

  const blogLinkClickEvent = (eventProperties: {
    url: string;
    referrer?: string;
  }) => {
    trackAmplitudeEvent("blog_link_click_view", eventProperties);
  };

  const productClickEvent = (eventProperties: {
    brand: string;
    position: number;
    product_id: string;
    original_price: number;
    price: number;
    product_name: string;
    retailer?: string | null;
    seo_url: string;
    referrer?: string;
    section_title?: string;
  }) => {
    trackAmplitudeEvent("product_click", {
      ...eventProperties,
      page: parsePage(getCurrentPage(), eventProperties.section_title),
      request_id: getRequestId(),
    });
  };

  const buyClickEvent = (eventProperties: {
    brand: string;
    product_id: string;
    original_price: number;
    price: number;
    sizes: string[];
    available_sizes: string[];
    product_name: string;
    retailer: string;
    seo_url: string;
    link: string;
    referrer?: string;
  }) => {
    trackAmplitudeEvent("buy_click", { ...eventProperties, page: "pdp" });
  };

  const saveEvent = (eventProperties: {
    brand: string;
    product_id: string;
    original_price: number;
    price: number;
    product_name: string;
    retailer: string;
    seo_url: string;
    collection_id: string;
    collection_name: string;
    referrer?: string;
  }) => {
    trackAmplitudeEvent("save", {
      ...eventProperties,
      page: getCurrentPage(),
      request_id: getRequestId(),
    });
  };

  const unsaveEvent = (eventProperties: {
    brand: string;
    product_id: string;
    original_price: number;
    price: number;
    product_name: string;
    retailer: string;
    seo_url: string;
    referrer?: string;
  }) => {
    trackAmplitudeEvent("unsave", {
      ...eventProperties,
      page: getCurrentPage(),
    });
  };

  const createCollectionEvent = (eventProperties: {
    collection_name: string;
    collection_id: string;
    referrer?: string;
  }) => {
    trackAmplitudeEvent("create_collection", {
      ...eventProperties,
      page: getCurrentPage(),
    });
  };

  const carouselItemClickEvent = (eventProperties: {
    carousel_name: string;
    item_text: string;
    item_search_query?: string;
    brand_id?: string;
    referrer?: string;
  }) => {
    trackAmplitudeEvent("carousel_item_click", eventProperties);
  };

  const createAccountEvent = (eventProperties: {
    method: "Email" | "Google";
  }) => {
    trackAmplitudeEvent("create_account", eventProperties);
  };

  const productThumbnailViewEvent = (eventProperties: {
    brand: string;
    position: number;
    product_id: string;
    original_price: number;
    price: number;
    product_name: string;
    retailer?: string | null;
    seo_url: string;
    section_title?: string;
    referrer?: string;
    newIn: boolean;
  }) => {
    const page = getCurrentPage();
    const seed_product_id = page === "pdp" ? router.query.id : undefined;
    trackAmplitudeEvent("product_thumbnail_view", {
      ...eventProperties,
      request_id: getRequestId(),
      page: parsePage(page, eventProperties.section_title),
      seed_product_id,
    });
  };

  const smartFilterClickEvent = (eventProperties: {
    query: string;
    preload: boolean;
  }) => {
    trackAmplitudeEvent("smart_filter_click_event", {
      ...eventProperties,
      request_id: getRequestId(),
      page: getCurrentPage(),
    });
  };

  const preferencePageViewEvent = (eventProperties: { step: number }) => {
    trackAmplitudeEvent("preference_page_view_event", {
      ...eventProperties,
      origin: preferenceOrigin,
    });
  };

  const preferenceNextClickEvent = (eventProperties: { step: number }) => {
    trackAmplitudeEvent("preference_next_click_event", {
      ...eventProperties,
    });
  };

  const preferenceExitClickEvent = (eventProperties: { step: number }) => {
    trackAmplitudeEvent("preference_exit_click_event", {
      ...eventProperties,
      origin: preferenceOrigin,
    });
  };

  const preferenceBannerViewEvent = () => {
    trackAmplitudeEvent("preference_banner_view_event");
  };

  const preferenceBannerClickEvent = () => {
    trackAmplitudeEvent("preference_banner_click_event");
  };

  return {
    searchEvent,
    rerunEvent,
    srpViewEvent,
    editsPageViewEvent,
    homePageViewEvent,
    aboutPageViewEvent,
    brandPageViewEvent,
    productPageViewEvent,
    productClickEvent,
    buyClickEvent,
    saveEvent,
    unsaveEvent,
    createCollectionEvent,
    carouselItemClickEvent,
    createAccountEvent,
    loginViewEvent,
    productThumbnailViewEvent,
    smartFilterClickEvent,
    blogsHomePageViewEvent,
    blogArticlePageViewEvent,
    blogLinkClickEvent,
    saveInternalOriginLocal,
    preferenceNextClickEvent,
    preferenceExitClickEvent,
    preferenceBannerViewEvent,
    preferencePageViewEvent,
    preferenceBannerClickEvent,
  };
};

export default useTracker;
